import { ButtonType, CheckBoxWithLabelWidget, CompactInlineAlertWidget, InlineAlertType, InlineAlertWidget } from '@tableau/widgets';
import React = require('react');
import _ = require('underscore');
import { ButtonWithSpinner } from '~commonInfra/components/ButtonWithSpinner';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Set } from '~commonInfra/utils/Set';

import { Formatters, Messages } from '../../../codegen/Localize';
import { L10n } from '../../../commonInfra/L10n/L10n';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { ResultHelper } from '../../../httpClient/utils/ResultHelper';
import { SiteRolePickerMenu } from '../../components/siteRolePicker/SiteRolePickerMenu';
import { filter } from '../../services/filters/SiteRoleFilter';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../../services/resources/SiteRoleOptions';
import { isIntegrationUserFeatureEnabled } from '../../users/serverApi/IntegrationUserFeature';
import { SiteRoles } from '../../users/serverApi/types/ISiteRole';
import { Groups } from '../Groups';
import { IGroupBasicInfo, IGroupInfo } from '../models/IGroupInfo';
import { GrantLicenseModes } from '../serverApi/requests/SyncActiveDirectoryGroupRequest';

export interface UpdateGrantLicenseModeDialogState {
  readonly siteRole: string;
  readonly grantLicenseModeCheckboxChecked: boolean;
  readonly busy: boolean;
}

export interface UpdateGrantLicenseModeDialogProps {
  readonly close: (result: any) => void;
  readonly dismiss: () => void;
  readonly groupsToSet: Set<IGroupInfo>;
  readonly isActiveDirectory?: boolean;
}

export class UpdateGrantLicenseModeDialog extends React.Component<
  UpdateGrantLicenseModeDialogProps,
  UpdateGrantLicenseModeDialogState
> {
  private siteRoleOptions = SiteRoleOptionsResource.instance.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator);
  private CheckBoxContainerStyle: React.CSSProperties = {
    marginLeft: '0px', marginTop: '8px'
  };
  private toaster: ToasterService = ToasterService.instance;

  public constructor(props: UpdateGrantLicenseModeDialogProps) {
    super(props);
    let siteRole: string = DefaultSiteRole;
    let checkboxChecked: boolean = false;
    if (props.groupsToSet.size() === 1) {
      let group: IGroupInfo = props.groupsToSet.getByIndex(0);
      siteRole = group.minimumSiteRole && group.minimumSiteRole !== SiteRoles.Unlicensed ? group.minimumSiteRole : siteRole;
      checkboxChecked = group.grantLicenseMode === GrantLicenseModes.ON_LOGIN;
    }
    this.state = {
      siteRole: siteRole,
      grantLicenseModeCheckboxChecked: checkboxChecked,
      busy: false
    };
  }

  private readonly getSiteRoleOptionBySiteRoleCallback = (siteRole: string): SiteRoleOption => {
    return SiteRoleOptionsResource.instance.getSiteRoleOptionBySiteRole(siteRole);
  };

  private readonly onOptionSelectedCallback = (siteRoleOption: SiteRoleOption): void => {
    this.onSiteRoleSelected(siteRoleOption);
  };

  public render() {
    return (
      <div>
        <div className="tb-dialog-title">{Messages.licenseUponLoginDialogTitle()}</div>
        <div className="tb-dialog-body-input-description">{Messages.confirmSetGroupsMinimumSiteRoleDesc({
          GROUP_NAME: L10n.elideIfLong(this.props.groupsToSet.first().name),
          GROUP_COUNT: this.props.groupsToSet.size(),
          GROUP_COUNT_LOC: Formatters.formatCount(this.props.groupsToSet.size())
        })}</div>
        {!this.props.isActiveDirectory &&
          <div>
            {this.getLicenseUponLoginCheckbox()}
            {this.getSiteRoleDropdown()}
          </div>
        }
        {this.props.isActiveDirectory &&
          <div>
            {this.getSiteRoleDropdown()}
            {this.state.siteRole === SiteRoles.Unlicensed &&
              <span className="tb-info-message">
                <InlineAlertWidget
                  children={Messages.confirmSetGroupsMinimumSiteRoleWarning()}
                  testId={'group-min-site-role-alert'}
                  type={InlineAlertType.Info}
                />
              </span>}
            {
              <span>
                {this.getLicenseUponLoginCheckbox()}
              </span>
            }
          </div>
        }
        <div className="tb-dialog-actions">
          <ButtonWithSpinner
            buttonType={ButtonType.Outline}
            onClick={this.props.dismiss}
            testId={'confirm-action-dialog-cancel'}
            children={Messages.cancel()}
            disabled={this.state.busy}
          />
          <span className="tb-margin-left-wide">
            <ButtonWithSpinner
              buttonType={ButtonType.Primary}
              showSpinner={this.state.busy}
              onClick={this.updateGrantLicenseModeCallback}
              testId={'confirm-action-dialog-submit'}
              children={Messages.confirm()}
            />
          </span>
        </div>
      </div>
    );
  }

  private handleCheckboxChange = (checked: boolean) => {
    this.setState({ grantLicenseModeCheckboxChecked: checked });
  };

  private getLicenseUponLoginCheckbox = (): JSX.Element => {
    return (<div><CheckBoxWithLabelWidget
      checked={this.state.grantLicenseModeCheckboxChecked}
      handleChange={this.handleCheckboxChange}
      containerStyle={this.CheckBoxContainerStyle}
      label={Messages.grantLicenseUponLoginCheckboxLabel()}
      testId={this.props.isActiveDirectory ? 'lul-update-ad-group' : 'lul-update-local-group'}
      disabled={(this.props.isActiveDirectory && this.state.siteRole === SiteRoles.Unlicensed)}
    />
    {this.state.grantLicenseModeCheckboxChecked && isIntegrationUserFeatureEnabled() &&
      <CompactInlineAlertWidget
        children={Messages.groupGrantRoleOnSignInIntegrationUsersInfo()}
        type={InlineAlertType.Info}
        testId='update-grant-license-integration-users-info'
        style={{ marginTop: 8, padding: 6 }}
      />
    }
    </div>);
  };

  private getSiteRoleDropdown = () => {
    return (<div className="tb-select-role-dropdown-container tb-row-with-icon-on-right tb-margin-top-wide">
      <SiteRolePickerMenu
        siteRoleOptions={this.siteRoleOptions}
        getSiteRoleOptionBySiteRole={this.getSiteRoleOptionBySiteRoleCallback}
        onOptionSelected={this.onOptionSelectedCallback}
        selectedRole={SiteRoleOptionsResource.instance.getSiteRoleOptionBySiteRole(this.state.siteRole)}
        disabledRoleOptions={this.props.isActiveDirectory ? [] : [SiteRoleOptionsResource.instance.getSiteRoleOptionBySiteRole(SiteRoles.Unlicensed)]}
        isDisabled={(!this.props.isActiveDirectory && !this.state.grantLicenseModeCheckboxChecked) || this.state.busy}
        showSiteRoleInfoLink={true} />
    </div>);

  };

  private updateGrantLicenseModeCallback = (): Promise<any> => {
    this.setState({ busy: true });
    const groups = _.map(this.props.groupsToSet.toArray(), (group: IGroupInfo) => group.id);
    const grantLicenseMode: GrantLicenseModes = this.getGrantLicenseMode();
    const siteRole = !this.props.isActiveDirectory && !this.state.grantLicenseModeCheckboxChecked ? SiteRoles.Unlicensed : this.state.siteRole;

    return this.updateGroupsMinimumSiteRole(groups, siteRole, grantLicenseMode);
  };

  private getGrantLicenseMode() {
    let grantLicenseMode: GrantLicenseModes;
    if (this.state.grantLicenseModeCheckboxChecked) {
      grantLicenseMode = GrantLicenseModes.ON_LOGIN;
    } else {
      grantLicenseMode = this.props.isActiveDirectory ? GrantLicenseModes.ON_SYNC : undefined;
    }
    return grantLicenseMode;
  }

  private handleSuccessResult(groups: Set<IGroupBasicInfo>, siteRole: string) {
    this.toaster.pop({
      type: ToastType.Success,
      text: Messages.updateGroupsSiteRoleSettingsSuccess({
        GROUP_NAME: L10n.elideIfLong(groups.first().name),
        COUNT: groups.size(),
        COUNT_LOC: Formatters.formatCount(groups.size())
      })
    });
  }

  private handleErrorResult(groups: Set<IGroupBasicInfo>, siteRole: string, result: IResult) {
    this.toaster.pop({
      type: ToastType.Error,
      text: Messages.updateGroupsMinimumSiteRoleError({
        GROUP_NAME: L10n.elideIfLong(groups.first().name),
        COUNT: groups.size(),
        SITE_ROLE: filter(siteRole),
        COUNT_LOC: Formatters.formatCount(groups.size())
      })
    });
    return Promise.reject(result); // for chaining errors
  }

  private onSiteRoleSelected(siteRoleOption: SiteRoleOption): void {
    let grantLicenseModeCheckboxChecked = (this.props.isActiveDirectory && siteRoleOption.role === SiteRoles.Unlicensed) ?
      false : this.state.grantLicenseModeCheckboxChecked;
    this.setState({ siteRole: siteRoleOption.role, grantLicenseModeCheckboxChecked });
  }

  public updateGroupsMinimumSiteRole = (groups: string[], siteRole: string, grantLicenseMode: GrantLicenseModes): Promise<any> => {
    return Groups.updateGroupsMinimumSiteRole(groups, siteRole, grantLicenseMode)
      .then(ResultHelper.rejectResultOnError())
      .then(() => {
        this.handleSuccessResult(this.props.groupsToSet, siteRole);
        this.props.close({});
      })
      .catch((result: IResult) => this.handleErrorResult(this.props.groupsToSet, siteRole, result));
  };
}
